import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import TableSkeleton from '../../../atoms/TableSkeleton'
import DDPWealthJourneyCategory from './DDPWealthJourneyCategory'

const useStyles = makeStyles(() => ({
  highlightArea: {
    padding: '1.5rem'
  },
  containerSkeleton: {
    height: '24rem',
    width: '100%',
    border: 'var(--border-neutral-emphasis)',
    borderRadius: 'var(--border-radius-lg)',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column'
  }
}))

/**
 * DDPWealthJourneyCategoryGrid - Display categories in a grid with activities
 *
 * Features:
 * - Fetches categories and entries from pipelines
 * - Displays categories in a responsive grid
 * - Each category manages its own activities
 * - Self-contained CRUD operations
 *
 * @component
 */
const DDPWealthJourneyCategoryGrid = ({
  categoriesPipeline,
  categoriesVariables,
  categoriesDataAccessor,
  entriesPipeline,
  entriesVariables,
  entriesDataAccessor,
  selectedYear,
  canEdit,
  clientId,
  onEntriesUpdated,
  gridColumns,
  className,
  style
}) => {
  const classes = useStyles()

  // Fetch categories
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError
  } = usePipelineDataContextWithVariables(
    categoriesPipeline,
    categoriesVariables
  )

  // Fetch entries
  const {
    data: entriesData,
    isLoading: entriesLoading,
    error: entriesError,
    refetch: refetchEntries
  } = usePipelineDataContextWithVariables(
    entriesPipeline,
    entriesVariables
  )

  // Extract data
  const categories = useMemo(() => {
    const cats = get(categoriesData, categoriesDataAccessor) || []
    return Array.isArray(cats) ? cats : []
  }, [categoriesData, categoriesDataAccessor])

  const entries = useMemo(() => {
    const ents = get(entriesData, entriesDataAccessor) || []
    return Array.isArray(ents) ? ents : []
  }, [entriesData, entriesDataAccessor])

  const loading = categoriesLoading || entriesLoading
  const error = categoriesError || entriesError

  // Handle activity updates
  const handleActivitiesUpdated = (categoryId) => (action, data) => {
    // Optionally refetch entries
    if (refetchEntries) {
      refetchEntries()
    }

    // Notify parent
    if (onEntriesUpdated) {
      onEntriesUpdated(categoryId, action, data)
    }
  }

  // Loading state
  if (loading) {
    return (
      <Grid container className={className} style={style}>
        {[...Array(4)].map((_, i) => (
          <Grid key={i} className={classes.highlightArea} xs={gridColumns} item>
            <div className={classes.containerSkeleton}>
              <TableSkeleton columns={1} rows={5} />
            </div>
          </Grid>
        ))}
      </Grid>
    )
  }

  // Error state
  if (error) {
    return (
      <Grid container className={className} style={style}>
        <Grid item xs={12}>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#D44333' }}>
            Error loading categories
          </div>
        </Grid>
      </Grid>
    )
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <Grid container className={className} style={style}>
        <Grid item xs={12}>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            No categories found
          </div>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container className={className} style={style}>
      {categories.map((category) => (
        <Grid
          key={category.categoryId}
          className={classes.highlightArea}
          xs={gridColumns}
          item
        >
          <DDPWealthJourneyCategory
            title={category.categoryName || category.name}
            categoryId={category.categoryId}
            allEntries={entries}
            entryDateYear={selectedYear}
            isLoading={loading}
            canEdit={canEdit}
            clientId={clientId}
            onActivitiesUpdated={handleActivitiesUpdated(category.categoryId)}
          />
        </Grid>
      ))}
    </Grid>
  )
}

DDPWealthJourneyCategoryGrid.propTypes = {
  /** Pipeline for fetching categories */
  categoriesPipeline: PropTypes.string,

  /** Variables for categories pipeline */
  categoriesVariables: PropTypes.object,

  /** Path to categories array in response */
  categoriesDataAccessor: PropTypes.string,

  /** Pipeline for fetching entries */
  entriesPipeline: PropTypes.string,

  /** Variables for entries pipeline */
  entriesVariables: PropTypes.object,

  /** Path to entries array in response */
  entriesDataAccessor: PropTypes.string,

  /** Selected year for filtering activities */
  selectedYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /** Whether user can edit activities */
  canEdit: PropTypes.bool,

  /** Client ID for creating entries */
  clientId: PropTypes.string,

  /** Callback when entries are updated */
  onEntriesUpdated: PropTypes.func,

  /** Grid columns (Material-UI grid size) */
  gridColumns: PropTypes.number,

  /** Additional CSS class */
  className: PropTypes.string,

  /** Inline styles */
  style: PropTypes.object
}

DDPWealthJourneyCategoryGrid.defaultProps = {
  categoriesPipeline: 'wealthjourney-categories',
  categoriesVariables: {},
  categoriesDataAccessor: 'data',
  entriesPipeline: 'wealthjourney-entries',
  entriesVariables: {},
  entriesDataAccessor: 'data',
  canEdit: false,
  clientId: null,
  onEntriesUpdated: null,
  gridColumns: 6,
  className: '',
  style: {}
}

export default DDPWealthJourneyCategoryGrid
